/**
 * Formats last seen timestamp in a human-readable format
 * Similar to WhatsApp and Facebook Messenger
 *
 * Format examples:
 * - "online" (currently online)
 * - "last seen just now" (< 1 minute)
 * - "last seen 2m ago" (< 1 hour)
 * - "last seen 2h ago" (< 24 hours)
 * - "last seen today at 3:45 PM" (today)
 * - "last seen yesterday at 10:30 AM" (yesterday)
 * - "last seen Monday at 2:15 PM" (this week)
 * - "last seen Jan 15 at 2:15 PM" (this year)
 * - "last seen Jan 15, 2024" (previous years)
 */

const formatLastSeen = (lastSeenDate, isOnline = false) => {
  // If user is currently online
  if (isOnline) {
    return "online";
  }

  // If no last seen date, show offline
  if (!lastSeenDate) {
    return "offline";
  }

  const now = new Date();
  const lastSeen = new Date(lastSeenDate);

  // Check if date is valid
  if (isNaN(lastSeen.getTime())) {
    return "offline";
  }

  const diffMs = now - lastSeen;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);

  // Active now (< 30 seconds)
  if (diffSeconds < 30) {
    return "just now";
  }

  // Active within last minute
  if (diffMinutes < 1) {
    return "just now";
  }

  // Active within last hour (show minutes)
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  // Check if it's today
  const isToday =
    now.getDate() === lastSeen.getDate() &&
    now.getMonth() === lastSeen.getMonth() &&
    now.getFullYear() === lastSeen.getFullYear();

  if (isToday) {
    // If within last 24 hours but today, show hours
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    // Show time for today (WhatsApp style: "today at 3:45 PM")
    return `today at ${formatTime(lastSeen)}`;
  }

  // Check if it's yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    yesterday.getDate() === lastSeen.getDate() &&
    yesterday.getMonth() === lastSeen.getMonth() &&
    yesterday.getFullYear() === lastSeen.getFullYear();

  if (isYesterday) {
    return `yesterday at ${formatTime(lastSeen)}`;
  }

  // Within last week (show day name - WhatsApp style: "Monday at 2:15 PM")
  if (diffDays < 7) {
    const dayName = getDayName(lastSeen);
    return `${dayName} at ${formatTime(lastSeen)}`;
  }

  // Check if it's this year (show date with time)
  const isThisYear = now.getFullYear() === lastSeen.getFullYear();

  if (isThisYear) {
    const monthDay = formatMonthDay(lastSeen);
    return `${monthDay} at ${formatTime(lastSeen)}`;
  }

  // Previous years (show full date without time for very old)
  const fullDate = formatFullDate(lastSeen);
  return fullDate;
};

/**
 * Formats time in 12-hour format with AM/PM
 */
const formatTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? "PM" : "AM";

  // Convert to 12-hour format
  if (hours > 12) {
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
  }

  // Add leading zero for single-digit minutes
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

  return `${hours}:${minutesStr} ${period}`;
};

/**
 * Gets the day name (Monday, Tuesday, etc.)
 */
const getDayName = (date) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[date.getDay()];
};

/**
 * Formats month and day (e.g., "Jan 15")
 */
const formatMonthDay = (date) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const day = date.getDate();
  return `${month} ${day}`;
};

/**
 * Formats full date with year (e.g., "Jan 15, 2024")
 */
const formatFullDate = (date) => {
  const monthDay = formatMonthDay(date);
  const year = date.getFullYear();
  return `${monthDay}, ${year}`;
};

/**
 * Gets a short version of last seen (for chat list previews)
 * Examples: "2m", "5h", "yesterday", "Mon", "Jan 15"
 */
export const getShortLastSeen = (lastSeenDate, isOnline = false) => {
  if (isOnline) return "now";
  if (!lastSeenDate) return "";

  const now = new Date();
  const lastSeen = new Date(lastSeenDate);

  if (isNaN(lastSeen.getTime())) return "";

  const diffMs = now - lastSeen;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "now";
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return getDayName(lastSeen).substring(0, 3); // Mon, Tue, etc.

  return formatMonthDay(lastSeen);
};

/**
 * Determines if last seen should be updated (for optimization)
 * Returns true if the display text would change
 */
export const shouldUpdateLastSeen = (lastSeenDate, previousUpdateTime = null) => {
  if (!lastSeenDate) return false;
  if (!previousUpdateTime) return true;

  const now = new Date();
  const lastSeen = new Date(lastSeenDate);
  const diffMs = now - lastSeen;
  const diffMinutes = Math.floor(diffMs / 60000);

  // Update every second if less than 1 minute
  if (diffMinutes < 1) return true;

  // Update every 30 seconds if less than 1 hour
  if (diffMinutes < 60) {
    const timeSinceLastUpdate = Date.now() - previousUpdateTime;
    return timeSinceLastUpdate >= 30000;
  }

  // Update every minute if less than 1 day
  if (diffMinutes < 1440) {
    const timeSinceLastUpdate = Date.now() - previousUpdateTime;
    return timeSinceLastUpdate >= 60000;
  }

  // Update every 5 minutes for older dates
  const timeSinceLastUpdate = Date.now() - previousUpdateTime;
  return timeSinceLastUpdate >= 300000;
};

export default formatLastSeen;
